/**
 * ICSC BOSS — Core Design System Components
 * All reusable UI building blocks for the platform.
 * Import from 'design-system' not directly from this file.
 */
import React, { useState } from "react";
import { colors, spacing, typography, radius, shadows, statusMap, animation } from "./tokens.js";

// ── Global keyframe injection ─────────────────────────────────────────────────
// Injects CSS animations once into the document head.
let _animsInjected = false;
function injectAnimations() {
  if (_animsInjected || typeof document === "undefined") return;
  _animsInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    @keyframes ds-fadeIn { from { opacity:0; } to { opacity:1; } }
    @keyframes ds-slideUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
    @keyframes ds-slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
    @keyframes ds-spin { to { transform: rotate(360deg); } }
    .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border-width: 0; }
    .ds-btn:focus-visible { outline: 3px solid #2563eb; outline-offset: 2px; }
    .ds-input:focus-visible, .ds-input:focus { outline: 3px solid #2563eb !important; outline-offset: 0 !important; box-shadow: 0 0 0 3px rgba(37,99,235,0.25) !important; border-color: #2563eb !important; }
    .ds-card-btn:focus-visible { outline: 3px solid #2563eb; outline-offset: 2px; }
    .ds-pill-btn:focus-visible { outline: 3px solid #2563eb; outline-offset: 2px; }
    .ds-tab-btn:focus-visible { outline: 3px solid #2563eb; outline-offset: -2px; }
    .ds-modal-close:focus-visible { outline: 3px solid #2563eb; outline-offset: 2px; }
  `;
  document.head.appendChild(style);
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// A. PageHeader
// Props: title, subtitle, actions (ReactNode, right-aligned)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function PageHeader({ title, subtitle, actions, breadcrumb }) {
  injectAnimations();
  return (
    <div
      className="ds-page-header"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: spacing.xl,
        animation: "ds-slideDown 200ms ease forwards",
      }}
    >
      <div>
        {breadcrumb && (
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: spacing.xs }}>
            {breadcrumb}
          </div>
        )}
        <h1 style={{
          margin: 0,
          fontSize: typography.fontSizes.xxxl,
          fontWeight: typography.fontWeights.bold,
          color: colors.textPrimary,
          lineHeight: typography.lineHeights.tight,
          fontFamily: typography.fontFamily,
        }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{
            margin: `${spacing.xs} 0 0`,
            fontSize: typography.fontSizes.md,
            color: colors.textSecondary,
            lineHeight: typography.lineHeights.normal,
          }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div
          className="ds-page-header-actions"
          style={{ display: "flex", gap: spacing.sm, alignItems: "center", flexShrink: 0, marginLeft: spacing.xl }}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// B. ActionBar
// Right-aligned action button container
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function ActionBar({ children, align = "right" }) {
  return (
    <div style={{
      display: "flex",
      gap: spacing.sm,
      justifyContent: align === "right" ? "flex-end" : align === "left" ? "flex-start" : "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    }}>
      {children}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// C. ContentSection
// Consistent page section with optional title
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function ContentSection({ title, subtitle, children, padded = false, style }) {
  return (
    <div style={{
      marginBottom: spacing.xl,
      ...(padded && {
        background: colors.surface,
        borderRadius: radius.lg,
        padding: spacing.xl,
        border: `1px solid ${colors.border}`,
        boxShadow: shadows.cardShadow,
      }),
      ...style,
    }}>
      {(title || subtitle) && (
        <div style={{ marginBottom: spacing.md }}>
          {title && (
            <h2 style={{
              margin: 0,
              fontSize: typography.fontSizes.xl,
              fontWeight: typography.fontWeights.semibold,
              color: colors.textPrimary,
            }}>
              {title}
            </h2>
          )}
          {subtitle && (
            <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSizes.md, color: colors.textSecondary }}>
              {subtitle}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// D. DataTable
// Consistent table: columns, rows, empty state, actions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function DataTable({ columns, rows, emptyMessage, loading, keyField = "id" }) {
  if (loading) {
    return (
      <div role="status" aria-live="polite" style={{ padding: spacing.xxl, textAlign: "center" }}>
        <div aria-hidden="true" style={{
          width: 32, height: 32, border: `3px solid ${colors.border}`,
          borderTopColor: colors.primary, borderRadius: "50%",
          animation: "ds-spin 0.7s linear infinite", margin: "0 auto 12px",
        }} />
        <div style={{ color: colors.textSecondary, fontSize: typography.fontSizes.md }}>Loading...</div>
      </div>
    );
  }

  // Separate action columns (no label or key="actions") from labelled data columns
  const actionCols = columns.filter((c) => !c.label || c.label === "" || c.key === "actions");
  const dataCols   = columns.filter((c) => c.label && c.label !== "" && c.key !== "actions");

  const emptyNode = (
    <div style={{ padding: `${spacing.xxl} ${spacing.xl}`, textAlign: "center", color: colors.textMuted }}>
      <div aria-hidden="true" style={{ fontSize: "32px", marginBottom: spacing.sm }}>📭</div>
      <div style={{ fontSize: typography.fontSizes.md }}>{emptyMessage || "No records found."}</div>
    </div>
  );

  return (
    <div
      role="region"
      aria-label="Data table"
      className="ds-table-wrap"
      style={{
        background: colors.surface,
        borderRadius: radius.lg,
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
        boxShadow: shadows.cardShadow,
      }}
    >
      {/* ── Desktop / tablet table ──────────────────────────────────────── */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={
                    col.priority === "low"    ? "col-priority-low"    :
                    col.priority === "medium" ? "col-priority-medium" : ""
                  }
                  style={{
                    padding: `${spacing.sm} ${spacing.md}`,
                    textAlign: col.align || "left",
                    fontSize: typography.fontSizes.sm,
                    fontWeight: typography.fontWeights.semibold,
                    color: colors.textSecondary,
                    whiteSpace: "nowrap",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(!rows || rows.length === 0) ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 0 }}>
                  {emptyNode}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={row[keyField] ?? i} style={{
                  borderBottom: `1px solid ${colors.border}`,
                  background: i % 2 === 0 ? colors.surface : colors.background,
                  transition: "background 120ms ease",
                }}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={
                        col.priority === "low"    ? "col-priority-low"    :
                        col.priority === "medium" ? "col-priority-medium" : ""
                      }
                      style={{
                        padding: `${spacing.sm} ${spacing.md}`,
                        fontSize: typography.fontSizes.md,
                        color: colors.textPrimary,
                        verticalAlign: "middle",
                        ...(col.align && { textAlign: col.align }),
                      }}
                    >
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Mobile card view (hidden on ≥768 px via CSS) ────────────────── */}
      <div className="ds-table-cards">
        {(!rows || rows.length === 0) ? emptyNode : rows.map((row, i) => (
          <div
            key={row[keyField] ?? i}
            style={{
              padding: spacing.md,
              borderBottom: i < rows.length - 1 ? `1px solid ${colors.border}` : "none",
              background: i % 2 === 0 ? colors.surface : colors.background,
            }}
          >
            {/* Labelled fields */}
            {dataCols.map((col) => (
              <div
                key={col.key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "5px 0",
                  borderBottom: `1px solid ${colors.border}44`,
                }}
              >
                <span style={{
                  fontSize: typography.fontSizes.xs,
                  fontWeight: typography.fontWeights.semibold,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  flexShrink: 0,
                  marginRight: spacing.sm,
                }}>
                  {col.label}
                </span>
                <span style={{ fontSize: typography.fontSizes.md, color: colors.textPrimary, textAlign: "right", flex: 1 }}>
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                </span>
              </div>
            ))}
            {/* Action buttons row */}
            {actionCols.length > 0 && (
              <div style={{
                display: "flex",
                gap: spacing.xs,
                flexWrap: "wrap",
                marginTop: spacing.sm,
                paddingTop: spacing.sm,
                borderTop: `1px solid ${colors.border}`,
              }}>
                {actionCols.map((col) => (
                  <span key={col.key}>
                    {col.render ? col.render(row[col.key], row) : null}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// E. StandardModal
// Consistent modal: header, body, footer
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function StandardModal({
  title,
  onClose,
  children,
  footer,
  width = 560,
  danger = false,
}) {
  injectAnimations();
  return (
    <div
      className="ds-modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.6)",
        backdropFilter: "blur(2px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 500,
        padding: spacing.lg,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ds-modal-title"
        className="ds-modal-box"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.surface,
          borderRadius: radius.xl,
          boxShadow: shadows.modalShadow,
          width: "100%",
          maxWidth: width,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          animation: "ds-slideUp 220ms ease forwards",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: `${spacing.lg} ${spacing.xl}`,
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <h3 id="ds-modal-title" style={{
            margin: 0,
            fontSize: typography.fontSizes.xl,
            fontWeight: typography.fontWeights.semibold,
            color: danger ? colors.danger : colors.textPrimary,
          }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="ds-modal-close"
            style={{
              background: "none", border: "none",
              fontSize: "20px", cursor: "pointer",
              color: colors.textMuted, lineHeight: 1,
              padding: spacing.xs, borderRadius: radius.sm,
              transition: "color 150ms", outline: "none",
            }}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: spacing.xl, overflowY: "auto", flex: 1 }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            padding: `${spacing.md} ${spacing.xl}`,
            borderTop: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: spacing.sm,
            flexShrink: 0,
            background: colors.backgroundAlt,
            borderRadius: `0 0 ${radius.xl} ${radius.xl}`,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// F. StatusBadge
// Maps status strings to consistent colour + label
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function StatusBadge({ status, customLabel, size = "md" }) {
  const map = statusMap[status] || { bg: colors.backgroundAlt, text: colors.textSecondary, label: status || "—" };
  const label = customLabel || map.label;

  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      background: map.bg,
      color: map.text,
      border: `1px solid ${map.border || map.bg}`,
      borderRadius: radius.pill,
      padding: size === "sm" ? "1px 8px" : "3px 10px",
      fontSize: size === "sm" ? typography.fontSizes.xs : typography.fontSizes.sm,
      fontWeight: typography.fontWeights.semibold,
      lineHeight: 1.6,
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// G. FormField
// Unified label + input + error layout
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function FormField({ label, htmlFor, required, error, helper, children, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px", ...style }}>
      {label && (
        <label htmlFor={htmlFor} style={{
          fontSize: typography.fontSizes.sm,
          fontWeight: typography.fontWeights.semibold,
          color: error ? colors.danger : colors.textPrimary,
        }}>
          {label}
          {required && <span aria-hidden="true" style={{ color: colors.danger, marginLeft: 2 }}>*</span>}
          {required && <span className="sr-only"> (required)</span>}
        </label>
      )}
      {children}
      {helper && !error && (
        <span style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>{helper}</span>
      )}
      {error && (
        <span style={{ fontSize: typography.fontSizes.xs, color: colors.danger }}>{error}</span>
      )}
    </div>
  );
}

// Standard input style (apply as inline style or export for spread)
// Add className="ds-input" to any native element using this style to get the
// WCAG-compliant focus ring injected by injectAnimations().
export const inputStyle = {
  width: "100%",
  padding: `${spacing.sm} ${spacing.md}`,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  fontSize: typography.fontSizes.md,
  color: colors.textPrimary,
  background: colors.surface,
  outline: "none",
  transition: "border-color 150ms, box-shadow 150ms",
  boxSizing: "border-box",
};

export const textareaStyle = {
  ...inputStyle,
  resize: "vertical",
  minHeight: "80px",
};

export const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// H. Card
// Surface card for dashboards, summaries, KPIs
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function Card({ children, padding = spacing.xl, accent, style, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...(onClick && {
        role: "button",
        tabIndex: 0,
        className: "ds-card-btn",
        onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } },
      })}
      style={{
        background: colors.surface,
        borderRadius: radius.lg,
        border: `1px solid ${colors.border}`,
        boxShadow: hovered && onClick ? shadows.md : shadows.cardShadow,
        padding,
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 200ms ease, transform 150ms ease",
        ...(onClick && { cursor: "pointer" }),
        ...(onClick && hovered && { transform: "translateY(-1px)" }),
        ...style,
      }}
    >
      {accent && (
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: accent,
        }} />
      )}
      {children}
    </div>
  );
}

// KPI variant of Card
export function KPICard({ label, value, change, color = colors.primary, icon, subtitle }) {
  return (
    <Card accent={color}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textSecondary, fontWeight: typography.fontWeights.medium, marginBottom: spacing.xs }}>
            {label}
          </div>
          <div style={{ fontSize: typography.fontSizes.xxxl, fontWeight: typography.fontWeights.bold, color: color, lineHeight: 1.1 }}>
            {value}
          </div>
          {subtitle && (
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginTop: spacing.xs }}>{subtitle}</div>
          )}
        </div>
        {icon && (
          <div aria-hidden="true" style={{
            fontSize: "24px",
            background: color + "18",
            borderRadius: radius.md,
            padding: spacing.sm,
            lineHeight: 1,
          }}>
            {icon}
          </div>
        )}
      </div>
      {change && (
        <div aria-live="polite" style={{
          marginTop: spacing.sm,
          fontSize: typography.fontSizes.sm,
          color: change.startsWith("+") ? colors.success : change.startsWith("-") ? colors.danger : colors.textMuted,
          fontWeight: typography.fontWeights.medium,
        }}>
          {change}
        </div>
      )}
    </Card>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// I. Button
// Consistent button with variants
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const BTN_VARIANTS = {
  primary:   { bg: colors.primary,   hover: colors.primaryHover,   text: "#fff", border: "transparent" },
  secondary: { bg: colors.backgroundAlt, hover: colors.border, text: colors.textPrimary, border: colors.border },
  danger:    { bg: colors.danger,    hover: colors.dangerHover,    text: "#fff", border: "transparent" },
  success:   { bg: colors.success,   hover: colors.successHover,   text: "#fff", border: "transparent" },
  warning:   { bg: colors.warning,   hover: colors.warningHover,   text: "#fff", border: "transparent" },
  ghost:     { bg: "transparent",    hover: colors.backgroundAlt,  text: colors.primary, border: "transparent" },
  outline:   { bg: "transparent",    hover: colors.primaryLight,   text: colors.primary, border: colors.primary },
};

const BTN_SIZES = {
  sm: { padding: "4px 12px", fontSize: typography.fontSizes.sm },
  md: { padding: "8px 18px", fontSize: typography.fontSizes.md },
  lg: { padding: "10px 24px", fontSize: typography.fontSizes.lg },
};

export function Btn({ variant = "primary", size = "md", disabled, loading, children, onClick, type = "button", form, style }) {
  const [hovered, setHovered] = useState(false);
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.primary;
  const s = BTN_SIZES[size] || BTN_SIZES.md;

  return (
    <button
      type={type}
      form={form}
      disabled={disabled || loading}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="ds-btn"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: spacing.xs,
        padding: s.padding,
        fontSize: s.fontSize,
        fontWeight: typography.fontWeights.semibold,
        fontFamily: typography.fontFamily,
        color: v.text,
        background: hovered && !disabled ? v.hover : v.bg,
        border: `1px solid ${v.border}`,
        borderRadius: radius.md,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "background 150ms ease, box-shadow 150ms ease",
        outline: "none",
        textDecoration: "none",
        lineHeight: 1.5,
        ...style,
      }}
    >
      {loading && (
        <span style={{
          display: "inline-block",
          width: 12, height: 12,
          border: "2px solid rgba(255,255,255,0.4)",
          borderTopColor: "#fff",
          borderRadius: "50%",
          animation: "ds-spin 0.7s linear infinite",
        }} />
      )}
      {children}
    </button>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// J. Alert / FlashMessage
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ALERT_STYLES = {
  success: { bg: colors.successLight,  text: colors.success,  border: "#86efac" },
  error:   { bg: colors.dangerLight,   text: colors.danger,   border: "#fca5a5" },
  warning: { bg: colors.warningLight,  text: colors.warning,  border: "#fde047" },
  info:    { bg: colors.infoLight,     text: colors.info,     border: "#7dd3fc" },
};

export function Alert({ type = "info", message, onClose }) {
  if (!message) return null;
  const s = ALERT_STYLES[type] || ALERT_STYLES.info;
  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        background: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
        borderRadius: radius.md,
        padding: `${spacing.sm} ${spacing.md}`,
        fontSize: typography.fontSizes.md,
        fontWeight: typography.fontWeights.medium,
        marginBottom: spacing.md,
        animation: "ds-slideDown 200ms ease forwards",
      }}
    >
      <span>{message}</span>
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Close alert"
          className="ds-modal-close"
          style={{ background: "none", border: "none", color: s.text, cursor: "pointer", fontSize: "16px", padding: "0 0 0 8px", lineHeight: 1, outline: "none" }}
        >
          <span aria-hidden="true">✕</span>
        </button>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// K. FilterPills
// Row of filter toggle buttons
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function FilterPills({ options, value, onChange, color = colors.primary }) {
  return (
    <div style={{ display: "flex", gap: spacing.xs, flexWrap: "wrap" }}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className="ds-pill-btn"
            style={{
              padding: "5px 14px",
              borderRadius: radius.pill,
              border: `1px solid ${active ? color : colors.border}`,
              background: active ? color : colors.surface,
              color: active ? "#fff" : colors.textSecondary,
              cursor: "pointer",
              fontSize: typography.fontSizes.sm,
              fontWeight: typography.fontWeights.medium,
              transition: "all 150ms ease",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// L. SectionDivider
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function SectionDivider({ label }) {
  if (!label) return <hr style={{ border: "none", borderTop: `1px solid ${colors.border}`, margin: `${spacing.xl} 0` }} />;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: spacing.md, margin: `${spacing.xl} 0` }}>
      <hr style={{ flex: 1, border: "none", borderTop: `1px solid ${colors.border}` }} />
      <span style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, whiteSpace: "nowrap", fontWeight: typography.fontWeights.medium }}>
        {label}
      </span>
      <hr style={{ flex: 1, border: "none", borderTop: `1px solid ${colors.border}` }} />
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// M. KPIGrid
// Responsive grid of KPI cards
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function KPIGrid({ kpis, columns = 4 }) {
  // Mobile: 2 cols → tablet: 3 cols → desktop: columns cols (capped 1–6)
  const cap = Math.min(Math.max(columns, 1), 6);
  return (
    <div className={`resp-kpis resp-kpis-${cap}`}>
      {kpis.map((kpi, i) => (
        <KPICard key={i} {...kpi} />
      ))}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// N. EmptyState
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function EmptyState({ icon = "📭", title = "No records found", subtitle, action }) {
  return (
    <div style={{
      textAlign: "center",
      padding: `${spacing.xxl} ${spacing.xl}`,
      color: colors.textMuted,
    }}>
      <div aria-hidden="true" style={{ fontSize: "40px", marginBottom: spacing.md }}>{icon}</div>
      <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.semibold, color: colors.textSecondary, marginBottom: spacing.xs }}>{title}</div>
      {subtitle && <div style={{ fontSize: typography.fontSizes.md, marginBottom: spacing.lg }}>{subtitle}</div>}
      {action}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// O. Tabs
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function Tabs({ tabs, activeTab, onChange }) {
  return (
    <div role="tablist" style={{ borderBottom: `2px solid ${colors.border}`, display: "flex", gap: 0, marginBottom: spacing.xl }}>
      {tabs.map((tab) => {
        const active = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={active}
            aria-controls={`tabpanel-${tab.key}`}
            id={`tab-${tab.key}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.key)}
            className="ds-tab-btn"
            style={{
              padding: `${spacing.sm} ${spacing.lg}`,
              background: "none",
              border: "none",
              borderBottom: active ? `2px solid ${colors.primary}` : "2px solid transparent",
              marginBottom: "-2px",
              fontSize: typography.fontSizes.md,
              fontWeight: active ? typography.fontWeights.semibold : typography.fontWeights.medium,
              color: active ? colors.primary : colors.textSecondary,
              cursor: "pointer",
              transition: "color 150ms, border-color 150ms",
              whiteSpace: "nowrap",
              outline: "none",
            }}
          >
            {tab.icon && <span aria-hidden="true" style={{ marginRight: spacing.xs }}>{tab.icon}</span>}
            {tab.label}
            {tab.count !== undefined && (
              <span aria-hidden="true" style={{
                marginLeft: spacing.xs,
                background: active ? colors.primaryLight : colors.backgroundAlt,
                color: active ? colors.primary : colors.textMuted,
                borderRadius: radius.pill,
                padding: "1px 7px",
                fontSize: typography.fontSizes.xs,
                fontWeight: typography.fontWeights.semibold,
              }}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
